import type { NotificationSeverity, NotificationView } from '@nadoutong/contracts';

/**
 * Local notification store (tenant #116, website side).
 *
 * The platform keeps 90 days of rows but has NO read-state ack this
 * round, so read/unread lives entirely here, in `localStorage`, scoped
 * per organization so switching orgs cannot cross-pollinate:
 *
 *   - `console.notifications.items.<orgId>`  — stored notices (JSON)
 *   - `console.notifications.cursor.<orgId>` — sync cursor (raw string)
 *
 * Two invariants inherited from the shipped desktop client, both
 * confirmed against the dev backend:
 *
 *   1. THE CURSOR IS AN OPAQUE STRING. `lastSeenAt` carries microsecond
 *      precision (6 fractional digits); parsing it through `Date` and
 *      re-serializing truncates to milliseconds, which re-fetches the
 *      newest row on every sync forever. Read raw, write raw.
 *   2. THE FINGERPRINT IS COMPARED, NEVER PARSED. Dedup is string
 *      equality on the server-issued fingerprint (`v1:` prefix today);
 *      an unknown future prefix is simply a different string and thus a
 *      different event, exactly as the contract wants.
 */

export interface StoredNotice {
  readonly id: string;
  readonly fingerprint: string;
  /**
   * Deliberately widened to `string`: the platform enum reserves kinds
   * that have no producer yet (`anomaly`, `status`) and may grow. An
   * unknown kind must degrade to a generic entry, never crash an
   * exhaustive switch.
   */
  readonly kind: string;
  readonly severity: NotificationSeverity;
  readonly title: string;
  readonly description: string;
  readonly createdAt: string;
  readonly read: boolean;
}

export const NOTICES_CHANGED_EVENT = 'nxtpath:notices-changed';

/** Local retention: the platform holds 90 days, the browser holds 200 rows. */
export const MAX_STORED_NOTICES = 200;

const ITEMS_KEY_PREFIX = 'console.notifications.items.';
const CURSOR_KEY_PREFIX = 'console.notifications.cursor.';

function readJson(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null || raw.length === 0) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota / private mode: the in-memory render still works, the next
    // sync simply starts from the last successfully persisted state.
  }
}

function emitNoticesChanged(): void {
  window.dispatchEvent(new CustomEvent(NOTICES_CHANGED_EVENT));
}

/** Unknown severity degrades to `info` — a future tier must not crash the page. */
export function normalizeSeverity(value: string): NotificationSeverity {
  return value === 'warning' || value === 'critical' ? value : 'info';
}

function isStoredNoticeLike(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { fingerprint?: unknown }).fingerprint === 'string' &&
    typeof (value as { title?: unknown }).title === 'string'
  );
}

function stringField(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value : '';
}

export function listNotices(scope: string): readonly StoredNotice[] {
  const raw = readJson(`${ITEMS_KEY_PREFIX}${scope}`);
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(isStoredNoticeLike).map((item) => ({
    id: stringField(item, 'id'),
    fingerprint: stringField(item, 'fingerprint'),
    kind: stringField(item, 'kind') || 'system',
    severity: normalizeSeverity(stringField(item, 'severity')),
    title: stringField(item, 'title'),
    description: stringField(item, 'description'),
    createdAt: stringField(item, 'createdAt'),
    read: item.read === true,
  }));
}

export function writeNotices(scope: string, notices: readonly StoredNotice[]): void {
  writeJson(`${ITEMS_KEY_PREFIX}${scope}`, notices);
  emitNoticesChanged();
}

export function unreadCount(scope: string): number {
  return listNotices(scope).filter((notice) => !notice.read).length;
}

export function markAllRead(scope: string): void {
  const notices = listNotices(scope);
  if (!notices.some((notice) => !notice.read)) {
    return;
  }
  writeNotices(
    scope,
    notices.map((notice) => ({ ...notice, read: true })),
  );
}

/**
 * Sync cursor, per org. Raw string in, raw string out — no `Date`
 * anywhere on this path (see the module header for why).
 */
export function readNotificationsCursor(scope: string): string {
  try {
    return window.localStorage.getItem(`${CURSOR_KEY_PREFIX}${scope}`) ?? '';
  } catch {
    return '';
  }
}

export function writeNotificationsCursor(scope: string, cursor: string): void {
  try {
    window.localStorage.setItem(`${CURSOR_KEY_PREFIX}${scope}`, cursor);
  } catch {
    // Same silent-degrade contract as writeJson.
  }
}

/**
 * Merge one server page into the local list. Pure — persistence is the
 * caller's job — so the dedup rules stay unit-testable.
 *
 * - Dedup is fingerprint string equality, nothing else.
 * - A fingerprint that already exists and arrives again means the
 *   server advanced `last_seen_at` (the same event recurred): replace
 *   the entry, move it to the top, and mark it unread again.
 * - `markRead: true` is the first-sync backfill: up to 90 days of
 *   history is not "events missed while away", so it inserts already
 *   read instead of lighting the dot with stale news.
 * - Pages arrive ascending; unshifting each row leaves the newest one
 *   on top, so the stored order is newest-first.
 */
export function mergeServerNotices(
  existing: readonly StoredNotice[],
  incoming: readonly NotificationView[],
  options: { readonly markRead: boolean },
): readonly StoredNotice[] {
  const next = [...existing];
  for (const item of incoming) {
    if (item.id.length === 0 || item.fingerprint.length === 0) {
      continue;
    }
    const index = next.findIndex((candidate) => candidate.fingerprint === item.fingerprint);
    if (index >= 0) {
      next.splice(index, 1);
    }
    next.unshift({
      id: item.id,
      fingerprint: item.fingerprint,
      kind: item.kind.length > 0 ? item.kind : 'system',
      severity: normalizeSeverity(item.severity),
      title: item.title,
      description: item.description,
      createdAt: item.createdAt,
      read: options.markRead,
    });
  }
  return next.slice(0, MAX_STORED_NOTICES);
}
